
import type React from "react"
import { useState, useRef, useEffect } from "react"
import { IoClose } from "react-icons/io5"
import "./chat.css"
import { useTheme } from "../../context"
import { useInterview } from "../../contexts/interview.context"
import { ConversationMode } from "../../types/interview.type"
import TypingDots from "./TypingDots"
import { Check, CheckCheck, Clock } from "lucide-react"
import { useAppSelector } from "../../redux/hooks"
import { toast } from "react-toastify"

interface ChatBoxProps {
  toggleChat: () => void
}

const ChatBox: React.FC<ChatBoxProps> = ({ toggleChat }) => {
  const { theme } = useTheme()

  useEffect(() => {
    // Add custom scrollbar styles with better mobile support
    const style = document.createElement("style")
    style.textContent = `
      /* Base scrollbar styles */
      .modern-scrollbar {
        overflow-y: auto;
        -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
        touch-action: pan-y !important; /* Prevents double-tap zoom */
      }
      
      /* Desktop-specific styles */
      @media (min-width: 768px) {
        .modern-scrollbar {
          scrollbar-width: thin; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        
        .modern-scrollbar::-webkit-scrollbar {
          width: 6px;
          display: none;
        }
        
        .modern-scrollbar:hover::-webkit-scrollbar {
          display: block;
        }
        
        .modern-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .modern-scrollbar::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"};
          border-radius: 20px;
        }
        
        .modern-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.2)"};
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [theme])

  const user = useAppSelector((state) => state.auth.user)
  const [numberOfRows, setNumberOfRows] = useState(1)
  const { interview, updateInterview, sendMessage: onSendMessage, finalTranscript, setFinalTranscript } = useInterview()
  const { showProcessing, setUp, chatMessages: messages, isMessageSent, selectedInterviweer } = interview

  const lastMessageRef = useRef<HTMLDivElement | null>(null)
  const [input, setInput] = useState("")
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  const chatEndRef = useRef<HTMLDivElement | null>(null)
  const isVideo = setUp?.conversationMode !== ConversationMode.CHAT

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    setInput(finalTranscript)
    if (finalTranscript) {
      updateInterview({
        isEnabledSubmitButton: true,
      })
    }
  }, [finalTranscript])

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }, 100)
    }
  }

  const sendMessage = async () => {
    const trimmedInput = input.trim()
    if (trimmedInput !== "") {
      setInput("")
      setFinalTranscript("")
      setNumberOfRows(1)
      await onSendMessage(trimmedInput)
    } else {
      toast.info("No input to send")
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
    }
  }, [input])

  return (
    <div className={`chatbox-container relative  ${theme} border border-accent-700/40 dark:border-accent-700/40`}>
    {/* {isVideo  && (
      <button
        className={`toggle-button absolute top-1 left-1  ${theme}`}
        onClick={toggleChat}
      >
        <IoClose />
      </button>
    )} */}

    <div className={`chat-scrollable modern-scrollbar min-h-full !touch-pan-y border border-accent-700/40 dark:border-accent-700/40 ${theme}`}>
      <div className="h-full pr-2 ">
        <div className="space-y-6">
          {[...messages, ...(showProcessing ? [{
            ...messages[0],
            sender: selectedInterviweer?.name,
            text: "",
            imageUrl: selectedInterviweer?.image,
          }] : [])].map((msg, index) => (
            <div
              ref={index === messages.length - 1 ? lastMessageRef : null}
              key={index}
              className={`message-row ${msg.sender === "user" ? "end" : "start"}`}
            >
              {msg.sender !== "user" && msg.imageUrl && (
                <div className="flex items-end mb-0.5">
                  <img
                    src={msg.imageUrl || "/placeholder.svg"}
                    alt="Bot"
                    className="user-avatar mr-3"
                  />
                </div>
              )}
              <div className={`message-bubble relative ${msg.sender === "user" ? `user ${theme}` : `bot ${theme}`}`}>
                <div className={`message-sender ${msg.sender === "user" ? "text-white" : "text-accent-500"}`}>
                  {msg.sender === "user" ? user?.firstName : msg.sender}
                </div>
                <div className="">
                  {msg.text || <TypingDots />}
                  {msg.sender === "user" && (
                    <div className="absolute text-gray-300 text-xs top-1 right-2">
                      {!isMessageSent && !showProcessing && index === messages.length - 1 ? (
                        <Clock size={15} />
                      ) : messages[messages.length - 1].text ? (
                        <CheckCheck size={15} />
                      ) : (
                        <Check size={13} />
                      )}
                    </div>
                  )}
                </div>
                {msg.text && (
                  <div className="timestamp text-slate-700 dark:text-slate-300">
                    {msg.date}
                  </div>
                )}
              </div>

              {msg.sender === "user" && (
                <div className="flex ml-3 items-end mb-0.5">
                  {msg.imageUrl ? (
                    <img
                      src={msg.imageUrl || "/placeholder.svg"}
                      alt="User"
                      className="user-avatar"
                    />
                  ) : (
                    <span className={`initials-circle ${theme}`}>
                      {user?.firstName?.[0] || "U"}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>
    </div>
  </div>
  )
}

export default ChatBox